import type { BetaSearch } from '../repositories/betaSearchRepository';

export const ANONYMOUS_INTERESTS_COOKIE = 'tldr_reader_interests';

export interface AnonymousInterestPref {
  id: string;
  included: boolean;
}

export function parseAnonymousInterestsCookie(cookieHeader: string | null | undefined): AnonymousInterestPref[] | null {
  if (!cookieHeader) return null;
  const match = cookieHeader
    .split(';')
    .map((s) => s.trim())
    .find((s) => s.startsWith(`${ANONYMOUS_INTERESTS_COOKIE}=`));
  if (!match) return null;
  try {
    const raw = decodeURIComponent(match.substring(ANONYMOUS_INTERESTS_COOKIE.length + 1));
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.filter(
      (item): item is AnonymousInterestPref =>
        Boolean(item) && typeof item.id === 'string' && typeof item.included === 'boolean',
    );
  } catch {
    return null;
  }
}

export function getAnonymousInterestsCookie(): AnonymousInterestPref[] | null {
  if (typeof document === 'undefined') return null;
  return parseAnonymousInterestsCookie(document.cookie);
}

export function clearAnonymousInterestsCookie(): void {
  if (typeof document !== 'undefined') {
    document.cookie = `${ANONYMOUS_INTERESTS_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
  }
}

export function setAnonymousInterestsCookie(interests: BetaSearch[]): void {
  if (typeof document === 'undefined') return;
  const prefs: AnonymousInterestPref[] = interests.map((i) => ({ id: i.id, included: i.included }));
  const encoded = encodeURIComponent(JSON.stringify(prefs));
  document.cookie = `${ANONYMOUS_INTERESTS_COOKIE}=${encoded}; path=/; max-age=31536000; SameSite=Lax`;
}

export function reconcileAnonymousInterests(
  defaultSearches: BetaSearch[],
  publicSearches: BetaSearch[],
  savedPrefs: AnonymousInterestPref[] | null,
): BetaSearch[] {
  if (!savedPrefs || savedPrefs.length === 0) {
    return defaultSearches;
  }
  const prefMap = new Map(savedPrefs.map((p) => [p.id, p.included]));
  const allKnown = new Map<string, BetaSearch>();
  for (const s of publicSearches) allKnown.set(s.id, s);
  for (const s of defaultSearches) allKnown.set(s.id, s);

  const result: BetaSearch[] = [];
  const addedIds = new Set<string>();

  for (const pref of savedPrefs) {
    const item = allKnown.get(pref.id);
    if (item) {
      result.push({ ...item, included: pref.included });
      addedIds.add(item.id);
    }
  }

  for (const def of defaultSearches) {
    if (!addedIds.has(def.id) && !prefMap.has(def.id)) {
      result.push(def);
      addedIds.add(def.id);
    }
  }

  return result;
}
